/**
 * SpiceTab — the SPICE tab: the current schedule at the top, then the
 * session list oldest to newest, one row per SPICE-band note (a session,
 * a decline, or a filed SPICE email summary), with a confirmed
 * spice_sessions row's facts layered on when one exists. Each row links
 * to its note. Allergies and conditions are the critical strip's to
 * show, not the tab's.
 *
 * Every reader of the record reads the schedule and whether it is
 * covered or paused ("Covered by Emerson Vale until 14 Oct", "Paused
 * until 14 Oct (Vacation)"). Its editor and the one "Cover or pause
 * visits" box render only for a viewer who may change a client's
 * schedule (canEditClientSchedule: manage_client_identity); a line
 * clinician, a SPICE therapist included, reads it and changes none of
 * it. The state is read on the practice day, so the day after the Until
 * date the tab reads regular with no one editing anything.
 *
 * Shown only when the client's engagement involves SPICE
 * (ClientRecordTabs' hideSpiceTab); gate mirrors the record's own
 * read_clinical_record gate at the handler. The tab strip already names
 * this tab "SPICE" — the content carries no second "SPICE" heading.
 */

import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canEditClientSchedule } from '@/lib/capabilities';
import { useSpiceSessions, type SpiceSessionWire } from '@/lib/spiceSessionsApi';
import {
  spiceVitalsToInput,
  useSetClientSpicePlan,
  type SpiceHoldReason,
  type SpiceScheduleVitals,
  type SpiceVisitPlanInput,
} from '@/lib/spiceVisitPlansApi';
import { effectiveHold } from '@/lib/spiceHold';
import { formatSpiceAway, sessionFromVitals } from '@/lib/spiceScheduleCopy';
import { practiceTodayIso } from '@/lib/practiceDate';
import { useAssignOptions } from '@/lib/workspace';
import { useSupportContact } from '@/hooks/useSupportContact';
import { pageWriteText } from '@/lib/errorCopy';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { Field } from '@/components/primitives/Field';
import { FLD } from '@/components/primitives/modalFormStyles';
import { proseDate } from '@/lib/proseDate';
import { SpiceScheduleEditDialog } from '@/components/clientRecord/VitalsBand';
import { SpiceScheduleSummary } from '@/components/clientRecord/SpiceScheduleSummary';
import type { ClientRecordSnapshot } from '@/lib/clientRecord';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

// Short adjectives: the ENGAGEMENT block's own label already carries the
// word "engagement", so the content never repeats it.
const ENGAGEMENT_LEVEL_LABELS: Record<string, string> = {
  low: 'Low',
  mixed: 'Mixed',
  good: 'Good',
};

// Mirrors api/_shared/aiTools/spiceActivityCatalog.ts's SPICE_MODALITY_ORDER
// (the FE cannot import an api/ module; SpiceAssessmentOutput.tsx carries
// the same mirror for the assessment report's own modality tables).
const MODALITY_LABELS: Record<string, string> = {
  cognitive: 'Cognitive',
  long_term_memory: 'Long Term Memory',
  orientation: 'Orientation',
  physical: 'Physical',
  toe_tappin_memories: "Toe-Tappin' Memories",
};

/** The house label geometry (src/index.css's ratified type scale, the
 *  CriticalStrip/VitalsBand precedent): uppercase, tracked, muted. */
const FACT_LABEL_CLS = 'text-label tracking-label font-semibold uppercase text-sage-400';

const KIND_LABEL: Record<SpiceSessionWire['kind'], string> = {
  session: 'Session',
  declined: 'Declined',
  filed: 'SPICE email summary, filed',
};

const HOLD_REASON_OPTIONS: { value: SpiceHoldReason; label: string }[] = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'medical', label: 'In hospital / medical' },
  { value: 'other', label: 'Other' },
];

const REGULAR: Pick<SpiceVisitPlanInput, 'status' | 'hold_reason' | 'hold_note' | 'status_until' | 'cover_roster_id'> = {
  status: 'active',
  hold_reason: null,
  hold_note: null,
  status_until: null,
  cover_roster_id: null,
};

/**
 * One box to cover or pause a client's visits. While the plan reads
 * regular, "Cover or pause visits" opens it: Reason, Note (required for
 * Other, and marked then), Until (optional, never before the practice
 * day; blank means no end date yet) and Covering therapist, whose first
 * option is "No one (visits pause)". With a therapist chosen the button
 * reads "Arrange cover" and saves the plan covered by that therapist;
 * with no one it reads "Pause visits" and saves it paused. The button
 * stays pressable: pressed with a missing note or a past Until it sends
 * nothing, says so under that box and moves focus there. While covered
 * the one action is "End cover", while paused "Resume visits"; both save
 * the plan regular. Each save is one PUT with the sessions as they stand
 * (spiceVitalsToInput, each session's own therapist included); a failure
 * shows the house sentence for it. The client's own therapist is never
 * offered as the cover. When a session names another therapist, a line
 * under the Covering therapist box says what the choice reaches: a cover
 * stands in for the client's own therapist's sessions only, and a pause
 * stops every session.
 */
function ScheduleAway({
  clientId,
  tenantSlug,
  plan,
  ownTherapistRosterId,
  ownTherapistName,
  today,
}: {
  clientId: string;
  tenantSlug: string;
  plan: SpiceScheduleVitals;
  ownTherapistRosterId: string | null;
  ownTherapistName: string | null;
  today: string;
}): ReactNode {
  const setPlan = useSetClientSpicePlan(tenantSlug);
  const { spiceTherapists } = useAssignOptions(tenantSlug);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<SpiceHoldReason>('vacation');
  const [note, setNote] = useState('');
  const [until, setUntil] = useState('');
  const [cover, setCover] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The gaps a press found; each clears on the next change to its box. */
  const [noteError, setNoteError] = useState(false);
  const [untilError, setUntilError] = useState(false);
  const noteRef = useRef<HTMLInputElement | null>(null);
  const untilRef = useRef<HTMLInputElement | null>(null);
  const contact = useSupportContact();

  const state = effectiveHold(plan.status, plan.holdReason, plan.statusUntil, { coverRosterId: plan.coverRosterId }, today)
    .status;
  const noteMissing = reason === 'other' && note.trim() === '';
  const untilInPast = until !== '' && until < today;
  const coverOptions = spiceTherapists.filter((t) => t.roster_id !== ownTherapistRosterId);
  // A session with a therapist of its own: the hint under the Covering
  // therapist box says what a cover or a pause reaches.
  const hasSessionTherapist = plan.sessions.some((s) => s.therapistRosterId !== undefined);
  const reachHint = !hasSessionTherapist
    ? null
    : cover !== ''
      ? `Covers the sessions with ${ownTherapistName ?? "the client's SPICE therapist"}. Sessions with another therapist go ahead.`
      : 'Pausing stops every session, including those with another therapist.';

  function send(change: Partial<SpiceVisitPlanInput>, onDone?: () => void): void {
    setError(null);
    setPlan.mutate(
      { clientId, input: { ...spiceVitalsToInput(plan, today), ...change } },
      { onSuccess: () => onDone?.(), onError: (err) => setError(pageWriteText(err, contact)) },
    );
  }

  function save(): void {
    if (noteMissing || untilInPast) {
      setNoteError(noteMissing);
      setUntilError(untilInPast);
      (noteMissing ? noteRef : untilRef).current?.focus();
      return;
    }
    const away = {
      hold_reason: reason,
      hold_note: note.trim() === '' ? null : note.trim(),
      status_until: until === '' ? null : until,
    };
    const change: Partial<SpiceVisitPlanInput> =
      cover !== ''
        ? { status: 'covered', ...away, cover_roster_id: cover }
        : { status: 'on_hold', ...away, cover_roster_id: null };
    send(change, () => setOpen(false));
  }

  return (
    <div className="mt-3 space-y-3">
      {state !== 'active' ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={setPlan.isPending}
          data-testid="spice-hold-end"
          onClick={() => send(REGULAR)}
        >
          {state === 'covered' ? 'End cover' : 'Resume visits'}
        </Button>
      ) : open ? (
        <div className="space-y-3 rounded-tight border border-sage-800/[14%] p-3" data-testid="spice-hold-form">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,16rem),1fr))] gap-3">
            <Field label="Reason" htmlFor="spice-hold-reason">
              <SelectField size="field"
                id="spice-hold-reason"
                data-testid="spice-hold-reason"
                className="w-full"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value as SpiceHoldReason);
                  setNoteError(false);
                }}
              >
                {HOLD_REASON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </SelectField>
            </Field>
            <Field
              label="Note"
              required={reason === 'other'}
              htmlFor="spice-hold-note"
              error={noteError ? 'Write a short note saying why.' : undefined}
            >
              <input
                ref={noteRef}
                id="spice-hold-note"
                data-testid="spice-hold-note"
                type="text"
                className={FLD}
                value={note}
                aria-required={reason === 'other' ? 'true' : undefined}
                aria-invalid={noteError ? 'true' : undefined}
                aria-describedby={noteError ? 'spice-hold-note-error' : undefined}
                onChange={(e) => {
                  setNote(e.target.value);
                  setNoteError(false);
                }}
              />
            </Field>
            <Field
              label="Until"
              htmlFor="spice-hold-until"
              error={untilError ? "Until can't be before today. Choose today or a later day." : undefined}
            >
              <DateInput
                ref={untilRef}
                id="spice-hold-until"
                data-testid="spice-hold-until"
                min={today}
                size="field" className="w-full"
                value={until}
                aria-invalid={untilError ? 'true' : undefined}
                aria-describedby={untilError ? 'spice-hold-until-error' : undefined}
                onChange={(e) => {
                  setUntil(e.target.value);
                  setUntilError(false);
                }}
              />
            </Field>
            <Field label="Covering therapist" htmlFor="spice-hold-cover" help={reachHint ?? undefined}>
              <SelectField size="field"
                id="spice-hold-cover"
                data-testid="spice-hold-cover"
                className="w-full"
                value={cover}
                onChange={(e) => setCover(e.target.value)}
              >
                <option value="">No one (visits pause)</option>
                {coverOptions.map((t) => (
                  <option key={t.roster_id} value={t.roster_id}>
                    {t.name}
                  </option>
                ))}
              </SelectField>
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={setPlan.isPending}
              loading={setPlan.isPending}
              data-testid="spice-hold-save"
              onClick={save}
            >
              {cover !== '' ? 'Arrange cover' : 'Pause visits'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="ghost" size="sm" data-testid="spice-hold-open" onClick={() => setOpen(true)}>
          Cover or pause visits
        </Button>
      )}

      {error !== null && (
        <p role="alert" className="text-xs text-pepper" data-testid="spice-hold-error">
          {error}
        </p>
      )}
    </div>
  );
}

/** One labelled fact block: the house label geometry above, body content
 *  below. The caller omits the block entirely when its fact is absent. */
function FactBlock({ label, testId, children }: { label: string; testId: string; children: ReactNode }): ReactNode {
  return (
    <div data-testid={testId}>
      <div className={FACT_LABEL_CLS}>{label}</div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  );
}

function ActivityLine({
  activity,
}: {
  activity: SpiceSessionWire['activities'][number];
}): ReactNode {
  // `what` (the therapist's own words) is the primary text once a session
  // carries it; the catalogue `activity` name rides after it as a muted
  // category. A row confirmed before `what` existed has only the
  // catalogue name, which then takes the primary slot alone — unchanged
  // from how every row read before `what` existed.
  const primary = activity.what ?? activity.activity;
  const category = activity.what ? activity.activity : null;
  return (
    <li>
      <div className="flex flex-wrap items-baseline">
        <span className="font-medium text-ink">{primary}</span>
        {category && (
          <>
            <span aria-hidden="true" className="text-xs text-ink-soft">
              {' '}
              ·{' '}
            </span>
            <span className="text-xs text-ink-soft">{category}</span>
          </>
        )}
      </div>
      {(activity.response || activity.modality) && (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-ink-soft">
          {activity.response && <span>{activity.response}</span>}
          {activity.modality && <Chip variant="neutral">{MODALITY_LABELS[activity.modality] ?? activity.modality}</Chip>}
        </div>
      )}
    </li>
  );
}

function SessionRow({ session }: { session: SpiceSessionWire }): ReactNode {
  const engagementLabel = session.engagement_level
    ? (ENGAGEMENT_LEVEL_LABELS[session.engagement_level] ?? session.engagement_level)
    : null;
  const hoursLabel = session.hours !== null ? `${session.hours}h` : null;

  return (
    <li data-testid="spice-session-row" className="p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`?tab=notes#note-${session.note_id}`}
            className="text-sm font-semibold text-sage-800 underline decoration-sage-700/30 underline-offset-2 hover:decoration-sage-700"
          >
            {proseDate(session.occurred_on)}
          </Link>
          <Chip variant="spice">{KIND_LABEL[session.kind]}</Chip>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          {session.therapist_name && <span data-testid="spice-session-therapist">{session.therapist_name}</span>}
          {session.raise_with_lead && (
            <Chip variant="risk" data-testid="spice-raise-mark">
              Raised with the program lead
            </Chip>
          )}
        </div>
      </div>

      <p className="text-sm text-ink-soft">{session.first_line}</p>

      {(session.activities.length > 0 || session.mood || engagementLabel || hoursLabel || session.next_plan) && (
        <div className="space-y-2">
          {session.activities.length > 0 && (
            <FactBlock label="Activities" testId="spice-session-activities">
              <ul className="space-y-1.5">
                {session.activities.map((a, i) => (
                  <ActivityLine key={`${session.note_id}-activity-${i}`} activity={a} />
                ))}
              </ul>
            </FactBlock>
          )}

          {session.mood && (
            <FactBlock label="Mood" testId="spice-session-mood">
              <span className="text-ink-soft">{session.mood}</span>
            </FactBlock>
          )}

          {(engagementLabel || hoursLabel) && (
            <FactBlock label="Engagement" testId="spice-session-engagement">
              <div className="flex flex-wrap items-center gap-x-2 text-ink-soft">
                {engagementLabel && <span>{engagementLabel}</span>}
                {hoursLabel && <span>{hoursLabel}</span>}
              </div>
            </FactBlock>
          )}

          {session.next_plan && (
            <FactBlock label="Plan for next time" testId="spice-session-plan">
              <span className="text-ink-soft">{session.next_plan}</span>
            </FactBlock>
          )}
        </div>
      )}
    </li>
  );
}

export interface SpiceTabProps {
  record: ClientRecordSnapshot;
}

export function SpiceTab({ record }: SpiceTabProps): ReactNode {
  const { tenantSlug, whoami } = useTenant();
  const { rows, isLoading, isError, error } = useSpiceSessions(tenantSlug, record.clientId);
  const [editingSchedule, setEditingSchedule] = useState(false);

  const { spiceSchedule } = record.vitals;
  const canEditSchedule = canEditClientSchedule(whoami);
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
  const [today] = useState(() => practiceTodayIso());
  const state = spiceSchedule
    ? effectiveHold(
        spiceSchedule.status,
        spiceSchedule.holdReason,
        spiceSchedule.statusUntil,
        { coverRosterId: spiceSchedule.coverRosterId },
        today,
      )
    : null;
  const away = state !== null && state.status !== 'active';
  const holdNote = spiceSchedule?.holdNote?.trim() ?? '';

  return (
    <div className="space-y-6" data-testid="spice-tab">
      <section className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-4 space-y-3">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink">Current schedule</h3>
            {canEditSchedule && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="spice-edit-schedule"
                onClick={() => setEditingSchedule(true)}
              >
                Edit schedule
              </Button>
            )}
          </div>
          <div className="text-sm text-ink-soft" data-testid="spice-tab-schedule">
            <SpiceScheduleSummary sessions={(spiceSchedule?.sessions ?? []).map(sessionFromVitals)} />
            {spiceSchedule && state !== null && state.status !== 'active' && (
              <p className="mt-1 font-medium text-ink" data-testid="spice-hold-status">
                {formatSpiceAway({
                  state: state.status,
                  hold_reason: state.hold_reason,
                  hold_note: spiceSchedule.holdNote,
                  status_until: spiceSchedule.statusUntil,
                  cover_name: spiceSchedule.coverName,
                })}
              </p>
            )}
            {spiceSchedule && away && state?.hold_reason !== 'other' && holdNote !== '' && (
              <p className="mt-0.5" data-testid="spice-hold-note-text">
                {holdNote}
              </p>
            )}
          </div>
          {canEditSchedule && spiceSchedule && (
            <ScheduleAway
              clientId={record.clientId}
              tenantSlug={tenantSlug}
              plan={spiceSchedule}
              ownTherapistRosterId={record.vitals.spiceTherapist?.rosterId ?? null}
              ownTherapistName={record.vitals.spiceTherapist?.displayName ?? null}
              today={today}
            />
          )}
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-sage-800">Sessions</h3>
        {lastRow && (
          <p className="text-sm text-ink-soft" data-testid="spice-last-session">
            Last session: {proseDate(lastRow.occurred_on)}
            {lastRow.kind === 'declined' ? ' (declined)' : ''}
          </p>
        )}
        <AsyncBoundary
          isLoading={isLoading}
          isError={isError}
          error={error}
          isEmpty={rows.length === 0}
          emptyHeadline="Post the first session note to start the list."
        >
          <div className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%]">
            <ul className="divide-y divide-sage-800/[14%]">
              {rows.map((session) => (
                <SessionRow key={session.note_id} session={session} />
              ))}
            </ul>
          </div>
        </AsyncBoundary>
      </section>

      {editingSchedule && (
        <SpiceScheduleEditDialog
          plan={spiceSchedule}
          clientId={record.clientId}
          tenantSlug={tenantSlug}
          spiceTherapistRosterId={record.vitals.spiceTherapist?.rosterId ?? null}
          onClose={() => setEditingSchedule(false)}
        />
      )}
    </div>
  );
}
